import { useEffect, useId, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ApiError, api, upgradeOf } from '../api/client.ts';
import type { Share, ShareKind, UpgradeRequired } from '../api/types.ts';
import { Icon, type IconName } from '../components/Icon.tsx';
import { PageHeader } from '../components/Shell.tsx';
import { UpgradeNotice, upgradePath } from '../components/UpgradePrompt.tsx';
import { Field, Modal, Segmented } from '../components/ui.tsx';
import { t, type MessageKey } from '../i18n/index.ts';
import { now, todayDay, todayString } from '../lib/clock.ts';
import { useIsDesktop, useOverview } from '../lib/hooks.ts';
import { decode } from '../lib/model.ts';
import { templatePreview, templateStructure } from '../lib/sharedMap.ts';
import { DraftBanner } from '../components/DraftBanner.tsx';
import { ownerName, useActivePlan, usePlan, useStore } from '../state/store.tsx';
import { useToast } from '../state/toast.tsx';
import { SharedMapView, type SharedMapSource } from './SharedMap.tsx';
import { useDocumentTitle } from '../lib/documentTitle.ts';

const KINDS: readonly { kind: ShareKind; icon: IconName; body: MessageKey }[] = [
  { kind: 'template', icon: 'receipt', body: 'shareNew.kind.template.body' },
  { kind: 'map', icon: 'forecast', body: 'shareNew.kind.map.body' },
  { kind: 'showcase', icon: 'whatIf', body: 'shareNew.kind.showcase.body' },
];

/** null = no expiry. */
const EXPIRY: Record<Exclude<ShareKind, 'showcase'>, { options: readonly (number | null)[]; initial: number }> = {
  map: { options: [7, 14, 30, 90], initial: 14 },
  template: { options: [7, 30, 90, null], initial: 30 },
};

const kindName = (kind: ShareKind | undefined) => t(`shareNew.kind.${kind ?? 'showcase'}`);

function createError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.isPlanTooLarge) return error.message;
    if (error.status === 409) return t('shareNew.error.limit', { n: error.body?.limit ?? 20 });
    if (error.status === 429) return t('shareNew.error.rate');
    if (error.status === 422) return t('shareNew.error.invalid', { detail: error.message });
  }
  return t('shareNew.error.generic');
}

const LAPSED: UpgradeRequired = { feature: 'lapsed', limit: null, tier: 'none', upgradeUrl: '' };

export function ShareCreateScreen() {
  useDocumentTitle(t('docTitle.share'));
  const wire = usePlan();
  const { me, refreshShares, setShares, refreshBilling, activePlanId, billing } = useStore();
  /** On a tier without custom expiry, links get the server's standard expiry and the picker explains why. */
  const standardExpiry = !!billing?.enforced && !billing.lapsed && !billing.limits.customShareExpiry;
  const lapsed = billing?.lapsed === true;
  const { plan: activePlan, canEdit, multiple } = useActivePlan();
  const desktop = useIsDesktop();
  const [kind, setKind] = useState<ShareKind>('template');
  const [titles, setTitles] = useState<Record<string, string>>({
    map: t('shareNew.defaultTitle.map'), template: t('shareNew.defaultTitle.template'),
  });
  const [expiry, setExpiry] = useState<Record<string, number | null>>({ map: EXPIRY.map.initial, template: EXPIRY.template.initial });
  const [understood, setUnderstood] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [upgrade, setUpgrade] = useState<UpgradeRequired | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const groupId = useId();

  useEffect(() => {
    void refreshShares();
  }, [refreshShares]);

  const owner = (me?.user.displayName ?? '').split(' ')[0] || null;
  const overview = useOverview();
  const source = useMemo<SharedMapSource | null>(() => {
    const asOf = todayString();
    if (kind === 'map') {
      const anchor = overview ? { cash: overview.breakdown.cash, investments: overview.breakdown.investments } : null;
      return { kind: 'map', asOf, plan: wire, anchor };
    }
    if (kind === 'template') {
      return { kind: 'template', asOf, template: { asOf, structure: templateStructure(wire), preview: templatePreview(decode(wire), todayDay()) } };
    }
    return null;
  }, [kind, wire, overview]);

  const choose = (next: ShareKind) => {
    setKind(next);
    setError(null);
    setUpgrade(null);
    setLink(null);
  };

  const create = async () => {
    if (kind === 'showcase') return;
    setBusy(true);
    setError(null);
    setUpgrade(null);
    try {
      const { share, url } = await api.createShare({
        title: titles[kind]?.trim() || t(`shareNew.defaultTitle.${kind}`),
        kind,
        ...(activePlanId ? { planId: activePlanId } : {}),
        ...(standardExpiry ? {} : { expiresInDays: expiry[kind] ?? null }),
      });
      setLink(url);
      setShares((all) => [share, ...all.filter((s) => s.token !== share.token)]);
      void refreshShares();
      void refreshBilling();
    } catch (e) {
      const needed = upgradeOf(e);
      if (needed) setUpgrade(needed);
      else setError(createError(e));
    } finally {
      setBusy(false);
    }
  };

  const meta = { title: titles[kind] || t('shareNew.defaultTitle.map'), ownerFirstName: owner, expiresAt: null };
  const options = (
    <section className="card card--pad-lg stack-md share-new__options" aria-label={t('shareNew.kinds')}>
      {kind === 'showcase' ? (
        <>
          <p className="share-new__note">{t('shareNew.showcaseNote')}</p>
          <Link to="/what-if" className="button button--outline-mint share-new__whatif"><Icon name="whatIf" size={16} />{t('shareNew.openWhatIf')}</Link>
        </>
      ) : lapsed ? (
        <UpgradeNotice upgrade={LAPSED} />
      ) : !canEdit ? (
        <p className="share-new__note"><Icon name="lock" size={14} />{t('shareNew.viewerBlocked', { name: ownerName(activePlan) ?? t('plans.role.owner') })}</p>
      ) : link ? (
        <>
          <LinkReady url={link} />
          <button type="button" className="button button--dashed" onClick={() => { setLink(null); setUnderstood(false); }}>
            <Icon name="plus" size={14} strokeWidth={2.5} />{t('shareNew.another')}
          </button>
        </>
      ) : (
        <>
          <Field label={t('shareNew.name')} htmlFor={`${groupId}-title`}>
            <input
              id={`${groupId}-title`}
              className="input"
              maxLength={120}
              value={titles[kind] ?? ''}
              onChange={(e) => setTitles((all) => ({ ...all, [kind]: e.target.value }))}
            />
          </Field>
          {standardExpiry ? (
            <div className="field">
              <span className="field__label">{t('shareNew.expiry')}</span>
              <p className="share-new__standard">
                {t('shareNew.expiryStandard', { n: EXPIRY[kind].initial })}
              </p>
              <p className="share-new__note share-new__plus">
                <Icon name="sparkle" size={14} />
                <span>{t('shareNew.expiryPlus')} <Link to={upgradePath('share_expiry')} className="text-link">{t('upgrade.cta')}</Link></span>
              </p>
            </div>
          ) : (
            <div className="field">
              <span className="field__label" id={`${groupId}-expiry`}>{t('shareNew.expiry')}</span>
              <Segmented
                label={t('shareNew.expiry')}
                variant="boxed"
                size="sm"
                options={EXPIRY[kind].options.map((d) => ({ value: d === null ? 'never' : String(d), label: d === null ? t('shareNew.never') : t('shareNew.days', { n: d }) }))}
                value={expiry[kind] === null ? 'never' : String(expiry[kind])}
                onChange={(v) => setExpiry((all) => ({ ...all, [kind]: v === 'never' ? null : Number(v) }))}
              />
            </div>
          )}
          {kind === 'map' ? (
            <div className="share-new__warning" role="note">
              <Icon name="alert" size={17} />
              <div className="stack-xs">
                <p>{t('shareNew.mapWarning')}</p>
                <label className="share-new__check">
                  <input type="checkbox" checked={understood} onChange={(e) => setUnderstood(e.target.checked)} />
                  {t('shareNew.understand')}
                </label>
              </div>
            </div>
          ) : (
            <div className="stack-xs">
              <p className="share-new__note"><Icon name="lock" size={14} />{t('shareNew.templateNote')}</p>
              <p className="share-new__note"><Icon name="flag" size={14} />{t('shareNew.templateFrozen')}</p>
            </div>
          )}
          {error && <p className="share-new__error" role="alert"><Icon name="alert" size={15} />{error}</p>}
          {upgrade && <UpgradeNotice upgrade={upgrade} />}
          <button
            type="button"
            className="button button--primary share-new__create"
            disabled={busy || (kind === 'map' && !understood)}
            onClick={() => void create()}
          >
            <Icon name="link" size={16} />{busy ? t('shareNew.creating') : t('shareNew.create')}
          </button>
        </>
      )}
    </section>
  );

  const previewCard = source && (
    <section className="share-new__preview" aria-labelledby={`${groupId}-preview`}>
      <div className="section-header">
        <h2 className="eyebrow" id={`${groupId}-preview`}>{t('shareNew.preview')}</h2>
        <span className="share-new__hint">{t('shareNew.previewHint')}</span>
      </div>
      <div className="share-new__frame" inert>
        <SharedMapView key={kind} source={source} meta={meta} preview />
      </div>
    </section>
  );

  return (
    <div className={desktop ? 'page share-new' : 'page page--mobile share-new'}>
      {desktop ? (
        <PageHeader eyebrow={t('shareNew.eyebrow')} title={t('shareNew.title')} subtitle={t('shareNew.subtitle')} />
      ) : (
        <header className="stack-xs">
          <h1 className="mobile-title">{t('shareNew.title')}</h1>
          <p className="page-subtitle">{t('shareNew.subtitle')}</p>
        </header>
      )}
      <DraftBanner />
      {multiple && activePlan && (
        <p className="share-new__from">
          <span className="eyebrow">{t('shareNew.from')}</span>
          <span className="share-new__from-name">{activePlan.name}</span>
        </p>
      )}
      <div className="share-new__grid">
        <div className="stack-lg">
          <fieldset className="share-kinds">
            <legend className="eyebrow">{t('shareNew.kinds')}</legend>
            {KINDS.map((k) => (
              <label key={k.kind} className="share-kind">
                <input type="radio" name={`${groupId}-kind`} value={k.kind} checked={kind === k.kind} onChange={() => choose(k.kind)} />
                <span className={`share-kind__icon share-kind__icon--${k.kind}`} aria-hidden="true"><Icon name={k.icon} size={18} /></span>
                <span className="share-kind__text">
                  <span className="share-kind__name">{kindName(k.kind)}</span>
                  <span className="share-kind__body">{t(k.body)}</span>
                </span>
              </label>
            ))}
          </fieldset>
          {options}
          {desktop && <ShareLinks />}
        </div>
        {previewCard}
        {!desktop && <ShareLinks />}
      </div>
    </div>
  );
}

function viewsLabel(views: number) {
  if (views <= 0) return t('shareNew.viewsNone');
  return views === 1 ? t('shareNew.viewsOne') : t('shareNew.views', { n: views });
}

function expiryLabel(share: Share, at: Date): { text: string; live: boolean } {
  if (share.revokedAt) return { text: t('connect.shares.revoked'), live: false };
  if (!share.expiresAt) return { text: t('connect.shares.never'), live: true };
  const days = Math.ceil((new Date(share.expiresAt).getTime() - at.getTime()) / 86_400_000);
  return days <= 0 ? { text: t('connect.shares.expired'), live: false } : { text: t('connect.shares.inDays', { n: days }), live: true };
}

function ShareLinks() {
  const { shares, setShares } = useStore();
  const toast = useToast();
  const [revoking, setRevoking] = useState<Share | null>(null);
  const id = useId();
  const at = now();
  const revoke = async (share: Share) => {
    setRevoking(null);
    setShares((all) => all.map((s) => (s.token === share.token ? { ...s, revokedAt: at.toISOString() } : s)));
    try {
      await api.revokeShare(share.token);
      toast.push({ tone: 'success', message: t('shareNew.revoked') });
    } catch {
      setShares((all) => all.map((s) => (s.token === share.token ? share : s)));
      toast.push({ tone: 'error', message: t('error.saveFailed') });
    }
  };
  return (
    <section className="card card--pad-lg stack-md" aria-labelledby={id}>
      <h2 className="eyebrow" id={id}>{t('shareNew.links')}</h2>
      {shares.length === 0 ? (
        <p className="muted">{t('shareNew.none')}</p>
      ) : (
        <ul className="share-list share-new__links">
          {shares.map((share) => {
            const status = expiryLabel(share, at);
            return (
              <li key={share.token} className={`share-list__item ${status.live ? '' : 'row--dim'}`}>
                <span className="share-list__text">
                  <span className="share-new__title-row">
                    <span className={`share-badge share-badge--${share.kind ?? 'showcase'}`}>{kindName(share.kind)}</span>
                    {status.live ? <Link to={`/s/${share.token}`} className="table-link">{share.title}</Link> : <span className="strong">{share.title}</span>}
                  </span>
                  <span className="share-list__meta">{viewsLabel(share.views)} · {status.text}</span>
                </span>
                {status.live && (
                  <button type="button" className="button button--sm button--outline" aria-label={t('shareNew.revokeLabel', { name: share.title })} onClick={() => setRevoking(share)}>
                    {t('shareNew.revoke')}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {revoking && (
        <Modal
          title={t('shareNew.revokeTitle')}
          onClose={() => setRevoking(null)}
          describedBy={`${id}-revoke`}
          footer={
            <>
              <button type="button" className="button button--ghost" onClick={() => setRevoking(null)}>{t('common.cancel')}</button>
              <button type="button" className="button button--primary" onClick={() => void revoke(revoking)}>{t('shareNew.revokeConfirm')}</button>
            </>
          }
        >
          <p id={`${id}-revoke`} className="modal__text">{t('shareNew.revokeBody', { name: revoking.title })}</p>
        </Modal>
      )}
    </section>
  );
}

/** A link that was just published: copy it, or open it as anyone with the link would. */
export function LinkReady({ url }: { url: string }) {
  const toast = useToast();
  const id = useId();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.push({ tone: 'success', message: t('common.copied') });
    } catch {
      toast.push({ tone: 'error', message: t('shareNew.error.generic') });
    }
  };
  return (
    <div className="stack-sm">
      <Field label={t('shareNew.ready')} htmlFor={id}>
        <div className="copy-field">
          <input id={id} className="input input--mono" readOnly value={url} onFocus={(e) => e.target.select()} />
          <button type="button" className="button button--outline" onClick={() => void copy()}>
            <Icon name="copy" size={14} />{t('common.copy')}
          </button>
        </div>
      </Field>
      <a className="text-link text-link--icon" href={url} target="_blank" rel="noopener noreferrer">
        <Icon name="eye" size={14} />{t('shareNew.viewOthers')}
      </a>
    </div>
  );
}
